import React, { useState, useEffect, useCallback } from 'react';
import { parseJinja2Variables } from '../../ExecutionTester/utils/jinja2Utils';
import {
  Paper,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import StatusChip from './StatusChip';
import {
  PlayArrow as ExecuteIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTemplateExecution } from '../hooks/useTemplateExecution';
import { debugError } from '../../../utils/debugLog';
import { useTemplateVersions } from '../hooks/useTemplateVersions';
import ExecutionResultDisplay from './ExecutionResultDisplay';
import ParameterValueInput from '../../ExecutionTester/components/ParameterValueInput';

/**
 * TemplateExecutionPanel Component
 * Collapsible panel for testing template execution within the editor
 */
const TemplateExecutionPanel = ({
  templateId,
  currentVersion,
  templateContent,
  isEditMode,
  templateName,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [inputData, setInputData] = useState({});
  const [allowDraft, setAllowDraft] = useState(true);

  // Load versions
  const { versions, loading: versionsLoading } =
    useTemplateVersions(templateId);

  // Execution hook
  const { executing, executionResult, error, executeTemplate, clearResult } =
    useTemplateExecution(
      templateId,
      selectedVersion?.version_number,
      templateName
    );

  // Parse template variables using shared utility with filtering
  const parseTemplateVariables = useCallback(content => {
    if (!content || typeof content !== 'string') return [];
    // Use the shared utility that includes validation and filtering
    try {
      return parseJinja2Variables(content);
    } catch (error) {
      // Error parsing template variables
      return [];
    }
  }, []);

  // Initialize input data from template variables
  useEffect(() => {
    if (templateContent) {
      const variables = parseTemplateVariables(templateContent);
      const initialData = {};
      variables.forEach(variable => {
        if (!inputData.hasOwnProperty(variable)) {
          initialData[variable] = '';
        }
      });
      if (Object.keys(initialData).length > 0) {
        setInputData(prev => ({ ...prev, ...initialData }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateContent, parseTemplateVariables]);

  // Set default version when versions load
  useEffect(() => {
    if (versions.length > 0 && !selectedVersion) {
      // Find current version or default to latest
      const versionToSelect =
        versions.find(v => v.version_number === currentVersion) ||
        versions[versions.length - 1];
      setSelectedVersion(versionToSelect);
    }
  }, [versions, currentVersion, selectedVersion]);

  // Get status badge color
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'deprecated':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  // Handle input change
  const handleInputChange = (key, value) => {
    setInputData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Add custom field
  const addCustomField = () => {
    const fieldName = `custom_${Object.keys(inputData).length + 1}`;
    setInputData(prev => ({
      ...prev,
      [fieldName]: '',
    }));
  };

  // Remove field
  const removeField = key => {
    setInputData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  };

  // Handle execution
  const handleExecute = async e => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!selectedVersion) {
      alert('Please select a version to test');
      return;
    }

    try {
      clearResult();
      await executeTemplate(inputData, {
        version_number: selectedVersion.version_number,
        allow_draft: allowDraft,
      });
    } catch (err) {
      // Error is handled by the hook
      debugError('Execution error:', err);
    }
  };

  // Get template variables
  const templateVariables = parseTemplateVariables(templateContent || '');

  if (!isEditMode) {
    return null;
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={(e, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <ExecuteIcon />
            <Typography variant="subtitle1" fontWeight="medium">
              Test Execution
            </Typography>
            {executionResult && (
              <Box sx={{ ml: 'auto' }}>
                <StatusChip label="Executed" color="success" />
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {/* Version Selector */}
            <Box mb={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Version</InputLabel>
                <Select
                  value={selectedVersion?.version_number || ''}
                  label="Version"
                  onChange={e => {
                    const version = versions.find(
                      v => v.version_number === e.target.value
                    );
                    setSelectedVersion(version);
                    clearResult();
                  }}
                  disabled={versionsLoading}
                >
                  {versions.map(version => (
                    <MenuItem
                      key={version.version_number}
                      value={version.version_number}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        width="100%"
                      >
                        <Typography>
                          Version {version.version_number}
                        </Typography>
                        <Box sx={{ ml: 'auto' }}>
                          <StatusChip
                            label={version.status || 'draft'}
                            color={getStatusColor(version.status)}
                            status={version.status}
                          />
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedVersion && (
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  <StatusChip
                    label={selectedVersion.status || 'draft'}
                    color={getStatusColor(selectedVersion.status)}
                    status={selectedVersion.status}
                  />
                </Box>
              )}
            </Box>

            {/* Input Data Fields */}
            {templateVariables.length > 0 && (
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle2">Input Data</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addCustomField}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                </Box>
                {templateVariables.map(variable => (
                  <ParameterValueInput
                    key={variable}
                    label={variable}
                    value={inputData[variable] || ''}
                    onChange={newValue => handleInputChange(variable, newValue)}
                    placeholder={`Enter value for ${variable}...`}
                    required
                    longTextThreshold={80}
                    sx={{ mb: 1 }}
                  />
                ))}
                {/* Custom fields */}
                {Object.keys(inputData)
                  .filter(key => !templateVariables.includes(key))
                  .map(key => (
                    <Box key={key} display="flex" gap={1} mb={1}>
                      <Box sx={{ flex: 1 }}>
                        <ParameterValueInput
                          label={key}
                          value={inputData[key] || ''}
                          onChange={newValue =>
                            handleInputChange(key, newValue)
                          }
                          placeholder={`Enter value for ${key}...`}
                          longTextThreshold={80}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeField(key)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
              </Box>
            )}

            {/* Advanced Options */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Advanced Options</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowDraft}
                      onChange={e => setAllowDraft(e.target.checked)}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2">
                        Allow Draft Execution
                      </Typography>
                      <Tooltip title="Enable to test draft versions. This uses the advanced execution endpoint.">
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                />
              </AccordionDetails>
            </Accordion>

            {/* Execute Button */}
            <Box mt={2}>
              <Button
                type="button"
                fullWidth
                variant="contained"
                startIcon={
                  executing ? <CircularProgress size={16} /> : <ExecuteIcon />
                }
                onClick={handleExecute}
                disabled={executing || !selectedVersion}
              >
                {executing ? 'Executing...' : 'Execute Template'}
              </Button>
            </Box>

            {/* Results Display */}
            {(executionResult || error) && (
              <ExecutionResultDisplay
                executionResult={executionResult}
                error={error}
              />
            )}

            {/* Info Alert */}
            {selectedVersion?.status === 'draft' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Testing draft version. This will use the advanced execution
                  endpoint with <code>allow_draft=true</code>.
                </Typography>
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default TemplateExecutionPanel;
